import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, SectionHeader } from "./ui";
import { PERIOD_LABELS } from "@/lib/constants";

const RES_STATUS = ["en_attente", "confirme", "traite", "annule"];
const RES_STATUS_LABELS = { en_attente: "En attente", confirme: "Confirmé", traite: "Traité", annule: "Annulé" };
const RES_FILTERS = [["tous", "Tous"], ["en_attente", "En attente"], ["confirme", "Confirmé"], ["annule", "Annulé"]];

export default function ReservationsAdmin() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("tous");
  const [reassign, setReassign] = useState(null);
  const [candidates, setCandidates] = useState({ date: null, slots: [] });
  const [paidRes, setPaidRes] = useState(null);
  const [allFiches, setAllFiches] = useState([]);
  const [checkedFiches, setCheckedFiches] = useState({});
  const load = useCallback(() => { api.get("/admin/reservations").then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/fiches").then((r) => setAllFiches(r.data)).catch(() => {}); }, []);

  const openPaid = (r) => {
    setPaidRes(r);
    const pre = {};
    (r.fiche_slugs || []).forEach((s) => { pre[s] = true; });
    setCheckedFiches(pre);
  };
  const confirmPaid = async () => {
    const slugs = Object.keys(checkedFiches).filter((k) => checkedFiches[k]);
    try {
      await api.post(`/admin/reservations/${paidRes.id}/mark-paid`, { fiche_slugs: slugs, send_email: true });
      toast.success(`Payé — ${slugs.length} fiche(s) envoyée(s) par email`);
      setPaidRes(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const setStatus = async (r, status) => { await api.patch(`/admin/reservations/${r.id}`, { status }); toast.success("Statut mis à jour"); load(); };
  const confirmRes = async (r) => {
    try { await api.post(`/admin/reservations/${r.id}/confirm`); toast.success("Confirmation envoyée à l'école"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const sendDevis = async (r) => {
    try { await api.post(`/admin/reservations/${r.id}/send-devis`); toast.success("Devis envoyé à l'enseignant"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-BE", { day: "numeric", month: "short", year: "numeric" }) : "-");

  const openReassign = async (r) => {
    setReassign(r); setCandidates({ date: null, slots: [] });
    try { const res = await api.get(`/admin/reservations/${r.id}/candidates`); setCandidates(res.data); } catch { setCandidates({ date: null, slots: [] }); }
  };
  const doReassign = async (slotId) => {
    try { await api.patch(`/admin/reservations/${reassign.id}/reassign`, { slot_id: slotId }); toast.success("Réservation réaffectée"); setReassign(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <Card>
      <SectionHeader title="Réservations" />
      <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-res-filters">
        {RES_FILTERS.map(([val, label]) => (
          <button key={val} onClick={() => setFilter(val)} data-testid={`admin-res-filter-${val}`}
            className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${filter === val ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {label}
          </button>
        ))}
      </div>
      {(() => { const visible = items.filter((r) => filter === "tous" || r.status === filter); return visible.length === 0 ? <p className="text-slate-500">Aucune reservation.</p> : (
        <div className="space-y-3" data-testid="admin-reservations-list">
          {visible.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-4 border border-slate-100 rounded-2xl p-4">
              <div className="flex-1 min-w-[200px]">
                <p className="font-bold text-slate-900">{r.item_title}</p>
                <p className="text-xs text-slate-500">{r.contact_name} · {r.email} · {r.school || "-"} · {fmt(r.created_at)}</p>
                <p className="text-xs text-slate-400 capitalize">{r.kind} · Date : {r.slot_date || r.preferred_date || "à définir"}{r.slot_period ? ` · ${PERIOD_LABELS[r.slot_period]}` : ""} · {r.students || "-"} enfants</p>
                {(r.school_year || r.class_name) && <p className="text-xs text-slate-500">Année : {r.school_year || "-"} · Classe : {r.class_name || "-"}</p>}
                {(r.formule || r.estimated_total != null) && <p className="text-xs font-semibold text-sky-700">{r.quote_ref ? `Réf. ${r.quote_ref} · ` : ""}Formule : {r.formule || "-"} · Total estimé : {r.estimated_total != null ? `${r.estimated_total} €` : "sur devis"}</p>}
                {r.animator_name && <p className="text-xs font-semibold text-sky-600 mt-0.5">Animateur : {r.animator_name}</p>}
              </div>
              {r.status !== "confirme" && (
                <button onClick={() => confirmRes(r)} data-testid={`admin-confirm-${r.id}`} className="text-xs font-bold px-3 py-2 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors">Confirmer &amp; envoyer</button>
              )}
              {r.formule && (
                <a href={`${api.defaults.baseURL}/reservations/${r.id}/devis.pdf`} target="_blank" rel="noreferrer" data-testid={`admin-devis-${r.id}`} className="text-xs font-bold px-3 py-2 rounded-full bg-sky-100 text-sky-700 hover:bg-sky-200 transition-colors">Devis PDF</a>
              )}
              {r.formule && (
                <button onClick={() => sendDevis(r)} data-testid={`admin-send-devis-${r.id}`} className="text-xs font-bold px-3 py-2 rounded-full bg-sky-500 text-white hover:bg-sky-600 transition-colors">Envoyer le devis</button>
              )}
              {r.kind === "atelier" && (
                <button onClick={() => openReassign(r)} data-testid={`admin-reassign-${r.id}`} className="text-xs font-bold px-3 py-2 rounded-full bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors">Réaffecter</button>
              )}
              <button onClick={() => openPaid(r)} data-testid={`admin-mark-paid-${r.id}`} className={`text-xs font-bold px-3 py-2 rounded-full transition-colors ${r.paid ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-amber-100 text-amber-700 hover:bg-amber-200"}`}>{r.paid ? "Payé ✓ · fiches" : "Marquer payé"}</button>
              <Select value={r.status} onValueChange={(v) => setStatus(r, v)}>
                <SelectTrigger className="rounded-xl w-40" data-testid={`admin-res-status-${r.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{RES_STATUS.map((s) => <SelectItem key={s} value={s}>{RES_STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
        </div>
      ); })()}

      <Dialog open={!!reassign} onOpenChange={(o) => !o && setReassign(null)}>
        <DialogContent data-testid="reassign-dialog">
          <DialogHeader>
            <DialogTitle>Réaffecter un animateur</DialogTitle>
            <DialogDescription>Choisissez un animateur disponible pour reprendre cette réservation.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            {candidates.date ? `Animateurs disponibles le ${new Date(candidates.date).toLocaleDateString("fr-BE", { day: "numeric", month: "long", year: "numeric" })} :` : "Créneaux disponibles :"}
          </p>
          {candidates.slots.length === 0 ? (
            <p className="text-sm text-slate-400 py-4" data-testid="reassign-empty">Aucun animateur disponible pour cette date. Un animateur doit d'abord proposer une disponibilité validée.</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {candidates.slots.map((sl) => (
                <button key={sl.id} onClick={() => doReassign(sl.id)} data-testid={`reassign-slot-${sl.id}`} className="w-full flex items-center justify-between border border-slate-100 rounded-xl p-3 hover:border-violet-400 hover:bg-violet-50 transition-colors">
                  <span className="font-semibold text-slate-800">{sl.animator_name}</span>
                  <span className="text-xs text-slate-500 capitalize">{new Date(sl.date).toLocaleDateString("fr-BE", { weekday: "short", day: "numeric", month: "short" })}</span>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!paidRes} onOpenChange={(o) => !o && setPaidRes(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle>Paiement & fiches de mission</DialogTitle>
            <DialogDescription>Cochez les ateliers payés par l'école. Un email avec le lien d'accès aux fiches sera envoyé.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
            {allFiches.map((f) => (
              <label key={f.slug} className={`flex items-center gap-3 border rounded-xl p-2.5 cursor-pointer transition-colors ${checkedFiches[f.slug] ? "border-sky-400 bg-sky-50" : "border-slate-100 hover:border-slate-200"}`} data-testid={`paid-fiche-${f.slug}`}>
                <input type="checkbox" checked={!!checkedFiches[f.slug]} onChange={(e) => setCheckedFiches({ ...checkedFiches, [f.slug]: e.target.checked })} className="w-4 h-4 accent-sky-500" data-testid={`paid-fiche-check-${f.slug}`} />
                <span className="text-sm font-semibold text-slate-800 flex-1">{f.title}</span>
                <span className="text-xs text-slate-400">{f.level}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setPaidRes(null)} className="text-sm font-bold px-4 py-2 rounded-full bg-slate-100 text-slate-700">Annuler</button>
            <button onClick={confirmPaid} data-testid="mark-paid-confirm" className="text-sm font-bold px-5 py-2 rounded-full bg-emerald-500 text-white hover:bg-emerald-600">Marquer payé & envoyer</button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
